<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="dashboard-main" ref="dashboardMain">
    <div class="dashboard-header" ref="dashboardHeader">
      <div class="dashboard-title"><b>Dashboard</b></div>
      <Button @click="addWidget" :disabled="dashboardLoading || !canAddWidget" class="add-widget-button">
        <img src="https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png" alt="" width="16" />
        Add Widget</Button
      >
    </div>
    <div class="dashboard-list-wrapper" :class="{ 'is-empty': widgetList && widgetList.length == 0 }">
      <transition-group
        v-if="widgetList && widgetList.length > 0"
        name="dashboard-widget"
        tag="div"
        class="dashboard-list"
      >
        <div
          v-for="widget in orderedWidgetList"
          :key="widget.id"
          :data-widget-id="widget.id"
          ref="dashboardItems"
          class="dashboard-grid-item"
          :style="getWidgetGridStyle(widget)"
        >
          <DashboardItem
            :widget="widget"
            :class="{ 'dashboard-widget-add-highlight': addWidgetHighlightId === widget.id }"
            @pointerdown.native.capture="clearWidgetHighlight(widget.id)"
            :dragging="dragWidgetId === widget.id"
            :draggingAtOriginal="dragWidgetId === widget.id && dragIsAtOriginal"
            @save="saveWidget"
            @resize="resizeWidget"
            @delete="deleteWidget"
            @widgetdragstart="onWidgetPointerStart(widget.id, $event)"
            @duplicate="duplicateWidget"
            :widgetList="dashboardItemConfigList"
            :dashboardAccess="dashboardAccess"
            :dashboardSasEnabled="dashboardSasEnabled"
          />
        </div>
      </transition-group>

      <div v-else-if="widgetList" class="dashboard-list dashboard-empty-list">
        <div class="dashboard-empty-placeholder d-flex flex-column align-items-center justify-content-start w-100 pt-5">
          <img :src="logo" />
          <div>Welcome to DSM 6G web application</div>
          <div>Please navigate yourself through the application using the left menu bar</div><br />
          <div>The dashboard is currently empty, please add a widget through the top 'Add Widget' button.</div>
        </div>
      </div>

      <div v-if="isDragging" class="dashboard-drop-overlay">
        <div class="dashboard-drop-ghost-slot is-target" :style="dragGhostStyle"></div>
      </div>
    </div>

    <div class="dashboard-json-actions">
      <input ref="dashboardImportInput" type="file" accept="application/json,.json" @change="importDashboardJson" />
      <Button @click="openDashboardImport" :disabled="dashboardLoading" class="dashboard-json-button">
        Import Dashboard
      </Button>
      <Button @click="exportDashboardJson" :disabled="dashboardLoading" class="dashboard-json-button">
        Export Dashboard
      </Button>
    </div>
  </div>
</template>

<script>
import DashboardItem from './DashboardItem.vue';
import dashboardItemConfig, { dashboardSasEnabled } from './dashboardItemConfig';
export default {
  components: { DashboardItem },
  computed: {
    logo() {
      return this.$store.state.loginDetails.CoLogo;
    },

    orderedWidgetList() {
      return this.getWidgetsByGridOrder(this.widgetList || []);
    },

    canAddWidget() {
      return this.dashboardItemConfigList.some(
        (item) =>
          !item.disabled &&
          (!this.dashboardSasEnabled ||
            item.skipSAS ||
            this.dashboardAccess.includes(`${item.controlKey}DisableNew`) ||
            this.dashboardAccess.includes(item.controlKey)),
      );
    },

    dragGhostStyle() {
      const rect = this.getDraggedPlacementRect(this.getActiveDragOrder());
      if (!rect) return { display: 'none' };
      return {
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        transform: `translate3d(${rect.left}px, ${rect.top}px, 0)`,
      };
    },

    dragIsAtOriginal() {
      if (!this.dragWidgetId) return false;
      return this.dragSnapshot.map((item) => item.id).join('|') ==
        this.getActiveDragOrder().map((item) => item.id).join('|');
    },
  },
  data: function () {
    return {
      widgetList: null,
      dashboardLoading: false,
      saveTimer: null,
      dragWidgetId: null,
      dragPointerId: null,
      dragPointerPoint: null,
      dragDropIndex: null,
      dragOriginalDropIndex: null,
      dragTargetOrder: null,
      dragSnapshot: [],
      dragGridMetrics: null,
      dragScrollFrame: null,
      dragShadowEl: null,
      dragShadowFrame: null,
      dragShadowPoint: null,
      dragShadowOffset: null,
      scrollAnimationFrame: null,
      scrollAnimationCancel: null,
      isDragging: false,
      addWidgetHighlightId: null,
      dashboardItemConfigList: [],
      dashboardAccess: [],
      dashboardSasEnabled,
    };
  },
  methods: {
    addWidget() {
      if (this.widgetList.length >= 24) {
        this.$showAlert('error', 'Widget Limit', 'Max widget limit reached.', [{ text: 'Ok' }]);

        return;
      }

      if (this.widgetList.length > 0 && this.widgetList[this.widgetList.length - 1].new) {
        const unsavedWidgetId = this.widgetList[this.widgetList.length - 1].id;
        this.$showAlert('warning', 'Add Widget', 'Please save the unsaved Add Widget before proceeding.', [
          { text: 'Ok' },
        ]);

        this.scrollToAndHighlightWidget(unsavedWidgetId);

        return;
      }

      const lastGridOrder = this.widgetList.reduce((max, item) => {
        const gridOrder = Number(item.gridOrder);
        return Number.isFinite(gridOrder) ? Math.max(max, gridOrder) : max;
      }, -1);
      const widgetId = crypto.randomUUID();
      this.addWidgetHighlightId = null;
      this.widgetList.push({ id: widgetId, new: true, gridOrder: lastGridOrder + 1 });
      this.scrollToAndHighlightWidget(widgetId);
    },

    scrollToAndHighlightWidget(widgetId) {
      this.addWidgetHighlightId = null;
      this.$nextTick(async () => {
        const highlightWidget = () => {
          if (!this.widgetList.some((item) => item.id == widgetId)) return;

          this.addWidgetHighlightId = widgetId;
        };
        await this.scrollToLastWidget(highlightWidget);
        if (!this._isDestroyed) highlightWidget();
      });
    },

    clearWidgetHighlight(widgetId) {
      if (this.addWidgetHighlightId == widgetId) this.addWidgetHighlightId = null;
    },

    cancelWidgetScroll() {
      if (this.scrollAnimationCancel) this.scrollAnimationCancel();
    },

    scrollToLastWidget(onApproach) {
      this.cancelWidgetScroll();
      const dashboardMain = this.$refs.dashboardMain;
      const dashboardItems = this.$refs.dashboardItems || [];
      const lastWidget = dashboardItems[dashboardItems.length - 1];
      const lastItem = lastWidget?.$el || lastWidget;
      const revealOnApproach = () => {
        if (typeof onApproach == 'function') onApproach();
      };
      if (!dashboardMain || !lastItem) {
        revealOnApproach();
        return Promise.resolve();
      }

      const headerOffset = this.$refs.dashboardHeader?.offsetHeight || 0;
      const targetTop = Math.max(lastItem.offsetTop - headerOffset - 8, 0);
      if (Math.abs(dashboardMain.scrollTop - targetTop) < 2) {
        revealOnApproach();
        return Promise.resolve();
      }

      dashboardMain.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });

      return new Promise((resolve) => {
        const startedAt = Date.now();
        let revealStarted = false;
        let completed = false;
        const finish = () => {
          if (completed) return;
          completed = true;
          if (this.scrollAnimationCancel === cancel) {
            this.scrollAnimationFrame = null;
            this.scrollAnimationCancel = null;
          }
          resolve();
        };
        const cancel = () => {
          if (completed) return;
          if (this.scrollAnimationCancel === cancel && this.scrollAnimationFrame) {
            cancelAnimationFrame(this.scrollAnimationFrame);
          }
          finish();
        };
        const checkScroll = () => {
          if (completed) return;
          const distanceToTarget = Math.abs(dashboardMain.scrollTop - targetTop);
          const reachedTarget = distanceToTarget < 2;
          const approachingTarget = distanceToTarget <= 80;
          const timedOut = Date.now() - startedAt >= 500;
          if (!revealStarted && (approachingTarget || timedOut)) {
            revealStarted = true;
            revealOnApproach();
          }
          if (reachedTarget || timedOut) {
            finish();
            return;
          }
          this.scrollAnimationFrame = window.requestAnimationFrame(checkScroll);
        };
        this.scrollAnimationCancel = cancel;
        this.scrollAnimationFrame = window.requestAnimationFrame(checkScroll);
      });
    },

    async deleteWidget(id, newWidget = false) {
      if (!newWidget) {
        const discard = await this.$showAlert(
          'warning',
          'Remove Widget',
          'Are you sure you want to remove this widget?',
          [{ text: 'Remove', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
        );

        if (discard == 'Cancel') return false;
      }
      this.widgetList = this.widgetList.filter((item) => item.id != id);
      this.saveDashboard();
    },

    saveWidget(data) {
      const widgetListCleaned = this.widgetList
        .filter((item) => !item.new)
        // eslint-disable-next-line no-unused-vars
        .map(({ id, new: _new, ...rest }) => rest);
      const widgetListSerialized = JSON.stringify(widgetListCleaned);

      if (this.widgetList.length >= 24) {
        this.$showAlert('error', 'Widget Limit', 'Max widget limit reached.', [{ text: 'Ok' }]);

        return;
      }

      if (widgetListSerialized.length > 8000) {
        this.$showAlert('error', 'Widget Limit', 'Max widget save file reached.', [{ text: 'Ok' }]);
        return;
      }
      const { id } = data || {};
      this.widgetList = this.widgetList.map((item) => (item.id == id ? { ...item, ...data, new: false } : item));
      this.saveDashboard();
    },

    resizeWidget(id, rowSpan) {
      this.widgetList = this.widgetList.map((item) => (item.id == id ? { ...item, rowSpan } : item));
      this.saveDashboard();
    },

    async duplicateWidget(widget) {
      const discard = await this.$showAlert(
        'warning',
        'Duplicate Widget',
        'Are you sure you want to duplicate this widget?',
        [{ text: 'Duplicate', color: '#6c8bfd' }, { text: 'Cancel' }],
      );

      if (discard == 'Cancel') return false;

      const widgetListCleaned = this.assignGridOrder(
        this.getWidgetsByGridOrder([...this.widgetList, widget]).filter((item) => !item.new),
      ).map(
        // eslint-disable-next-line no-unused-vars
        ({ id, new: _new, ...rest }) => rest,
      );
      const widgetListSerialized = JSON.stringify(widgetListCleaned);

      if (widgetListSerialized.length > 8000) {
        this.$showAlert('error', 'Widget Limit', 'Max widget limit reached.', [{ text: 'Ok' }]);
        return;
      }
      if (this.widgetList.length >= 24) {
        this.$showAlert('error', 'Widget Limit', 'Max widget limit reached.', [{ text: 'Ok' }]);
        return;
      }

      // eslint-disable-next-line no-unused-vars
      const { id, new: _new, ...rest } = widget;
      const duplicate = { ...rest, id: crypto.randomUUID() };

      const orderedWidgets = this.getWidgetsByGridOrder(this.widgetList);
      const originalIndex = orderedWidgets.findIndex((item) => item.id === id);
      orderedWidgets.splice(originalIndex + 1, 0, duplicate);
      this.widgetList = this.assignGridOrder(orderedWidgets);

      this.saveDashboard();
    },

    getWidgetsByGridOrder(widgets = []) {
      return widgets
        .map((item, index) => ({ ...item, _sourceIndex: index }))
        .sort((a, b) => {
          const aOrder = Number(a.gridOrder);
          const bOrder = Number(b.gridOrder);
          const aHasOrder = Number.isFinite(aOrder);
          const bHasOrder = Number.isFinite(bOrder);
          if (aHasOrder && bHasOrder && aOrder != bOrder) return aOrder - bOrder;
          if (aHasOrder != bHasOrder) return aHasOrder ? -1 : 1;
          return a._sourceIndex - b._sourceIndex;
        })
        .map(({ _sourceIndex, ...item }) => item);
    },

    assignGridOrder(widgets = []) {
      return widgets.map((item, index) => ({ ...item, gridOrder: index }));
    },

    normalizeWidgetRowSpan(value) {
      return Number(value) == 2 ? 2 : 1;
    },

    getWidgetGridStyle(widget) {
      const gridOrder = Number(widget?.gridOrder);
      return {
        gridRow: `span ${this.normalizeWidgetRowSpan(widget?.rowSpan)}`,
        order: Number.isFinite(gridOrder) ? gridOrder : 0,
      };
    },

    normalizeGridOrder(widgets = []) {
      return this.assignGridOrder(this.getWidgetsByGridOrder(widgets));
    },

    normalizeWidgetListOrder() {
      this.widgetList = this.normalizeGridOrder(this.widgetList || []);
      return this.widgetList;
    },

    getDashboardSaveJson(widgets = this.widgetList || []) {
      return this.normalizeGridOrder(widgets.filter((item) => !item.new)).map(
        // eslint-disable-next-line no-unused-vars
        ({ id, new: _new, ...rest }) => rest,
      );
    },

    exportDashboardJson() {
      const dashboardJson = JSON.stringify(this.getDashboardSaveJson(), null, 2);
      const blob = new Blob([dashboardJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard-${new Date().formatString('yyyyMMdd-HHmmss')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    openDashboardImport() {
      this.$refs.dashboardImportInput.value = '';
      this.$refs.dashboardImportInput.click();
    },
    async importDashboardJson(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const importedWidgets = JSON.parse(text);
        if (!Array.isArray(importedWidgets)) throw new Error('Dashboard JSON must be an array.');

        const discard = await this.$showAlert(
          'warning',
          'Import Dashboard',
          'Importing this file will replace the current dashboard.',
          [{ text: 'Import', color: '#6c8bfd' }, { text: 'Cancel' }],
        );
        if (discard == 'Cancel') return;

        this.widgetList = this.normalizeGridOrder(importedWidgets).map((item) => ({
          ...item,
          id: crypto.randomUUID(),
        }));
        this.saveDashboard();
      } catch (err) {
        this.$showAlert('error', 'Import Dashboard Failed', err.message || 'Invalid dashboard JSON.', [{ text: 'Ok' }]);
      }
    },

    saveDashboard(immediate = false) {
      const orderedWidgets = this.normalizeWidgetListOrder();
      this.$store.commit('updateDashboard', orderedWidgets);
      const widgetListCleaned = this.getDashboardSaveJson();
      const widgetListSerialized = JSON.stringify(widgetListCleaned);

      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      const persist = () =>
        this.$apiCallV2('/Dashboard/SaveDashboard', { settingsvalue: widgetListSerialized }, 'POST', {
          message: `Save Dashboard failed`,
        });
      if (immediate) return persist();

      this.saveTimer = setTimeout(() => {
        this.saveTimer = null;
        persist();
      }, 500);
    },

    async getDashboard() {
      this.dashboardLoading = true;
      if (this._isDestroyed) return;
      const result = await this.$apiCallV2('/Dashboard/GetDashboard', {}, 'POST', {
        message: `Get Dashboard failed`,
      });
      if (this._isDestroyed) return;
      const resultSerialized = result[0]?.SettingsValue ? JSON.parse(result[0].SettingsValue) : [];
      const widgetListAddID =
        this.normalizeGridOrder(resultSerialized)?.map((item) => ({ ...item, id: crypto.randomUUID() })) || [];
      this.$store.commit('updateDashboard', widgetListAddID);
      this.dashboardLoading = false;
    },

    handleUnload() {
      if (this.saveTimer) this.saveDashboard(true);
    },
    getDashboardListElement() {
      return this.$refs.dashboardMain?.querySelector('.dashboard-list');
    },
    getDashboardItemElements() {
      const items = this.$refs.dashboardItems || [];
      return (Array.isArray(items) ? items : [items])
        .map((item) => item?.$el || item)
        .filter((item) => item?.nodeType == 1);
    },
    buildDragSnapshot() {
      const orderedWidgets = this.getWidgetsByGridOrder(this.widgetList || []);
      const widgetMap = orderedWidgets.reduce((acc, widget, index) => {
        acc[widget.id] = { widget, index };
        return acc;
      }, {});
      const listEl = this.getDashboardListElement();
      const listRect = listEl?.getBoundingClientRect();
      const styles = listEl ? window.getComputedStyle(listEl) : null;
      const rowHeight = parseFloat(styles?.gridAutoRows) || 0;
      const columnGap = parseFloat(styles?.columnGap) || 0;
      const rowGap = parseFloat(styles?.rowGap) || columnGap;
      const columns = styles?.gridTemplateColumns?.split(/\s+/).filter(Boolean).length || 1;
      const columnWidth = listEl ? (listEl.clientWidth - columnGap * (columns - 1)) / columns : 0;
      this.dragGridMetrics = { columns, columnGap, rowGap, rowHeight, columnWidth };

      return this.getDashboardItemElements()
        .map((el) => {
          const rect = el.getBoundingClientRect();
          const entry = widgetMap[el.dataset.widgetId];
          const widget = entry?.widget;
          const left = rect.left - (listRect?.left || 0);
          const top = rect.top - (listRect?.top || 0);
          return {
            id: el.dataset.widgetId,
            left,
            right: left + rect.width,
            top,
            bottom: top + rect.height,
            width: rect.width,
            height: rect.height,
            rowSpan: this.normalizeWidgetRowSpan(widget?.rowSpan),
            gridOrder: entry?.index ?? 0,
          };
        })
        .filter((item) => item.id)
        .sort((a, b) => a.gridOrder - b.gridOrder);
    },
    startWidgetDrag(widgetId, event) {
      if (!event || this.isDragging || (event.pointerType == 'mouse' && event.button != 0)) return false;
      if (event.cancelable) event.preventDefault();
      if (window.getSelection().toString()) {
        window.getSelection().removeAllRanges();
      }
      this.dragSnapshot = this.buildDragSnapshot();
      if (!this.dragSnapshot.some((item) => item.id == widgetId)) return false;
      this.dragWidgetId = widgetId;
      this.dragPointerId = event.pointerId;
      this.dragOriginalDropIndex = this.dragSnapshot.findIndex((item) => item.id == widgetId);
      this.dragDropIndex = this.dragOriginalDropIndex;
      this.isDragging = true;
      this.createDragShadow(widgetId, event);
      this.updateDragPosition(event);
      return true;
    },
    onWidgetPointerStart(widgetId, event) {
      if (!this.startWidgetDrag(widgetId, event)) return;
      window.addEventListener('pointermove', this.onWidgetPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onWidgetPointerEnd);
      window.addEventListener('pointercancel', this.onWidgetPointerCancel);
    },
    setDragDropIndex(targetIndex) {
      const visibleCount = this.getDragItems().length;
      const nextIndex = Math.max(0, Math.min(targetIndex, visibleCount));
      this.dragTargetOrder = null;
      if (this.dragDropIndex == nextIndex) return;
      this.dragDropIndex = nextIndex;
    },
    getDragItems() {
      return this.dragSnapshot.filter((item) => item.id != this.dragWidgetId);
    },
    getDraggedSnapshotItem() {
      return this.dragSnapshot.find((item) => item.id == this.dragWidgetId);
    },
    getDashboardListPoint(clientX, clientY) {
      const listRect = this.getDashboardListElement()?.getBoundingClientRect();
      return {
        x: clientX - (listRect?.left || 0),
        y: clientY - (listRect?.top || 0),
      };
    },
    getDragCandidateOrder(dropIndex) {
      const dragged = this.getDraggedSnapshotItem();
      if (!dragged) return [];
      const items = [...this.getDragItems()];
      items.splice(Math.max(0, Math.min(dropIndex, items.length)), 0, dragged);
      return items;
    },
    getActiveDragOrder() {
      if (this.dragTargetOrder) return this.dragTargetOrder;
      return this.dragDropIndex === null ? [] : this.getDragCandidateOrder(this.dragDropIndex);
    },
    getSameHeightDropOrder(targetId) {
      const dragged = this.getDraggedSnapshotItem();
      const target = this.dragSnapshot.find((item) => item.id == targetId);
      if (!dragged || !target || dragged.rowSpan != target.rowSpan) return null;

      const matchingItems = this.dragSnapshot.filter((item) => item.rowSpan == dragged.rowSpan);
      const targetIndex = matchingItems.findIndex((item) => item.id == targetId);
      const reorderedItems = matchingItems.filter((item) => item.id != dragged.id);
      reorderedItems.splice(targetIndex, 0, dragged);

      let matchingIndex = 0;
      return this.dragSnapshot.map((item) =>
        item.rowSpan == dragged.rowSpan ? reorderedItems[matchingIndex++] : item,
      );
    },
    simulateDenseGrid(items) {
      const columns = this.dragGridMetrics?.columns || 1;
      const occupied = [];
      return items.map((item) => {
        const rowSpan = this.normalizeWidgetRowSpan(item.rowSpan);
        let row = 0;
        let column = 0;
        let found = false;
        while (!found) {
          for (column = 0; column < columns; column++) {
            const fits = Array.from({ length: rowSpan }, (_, offset) => !occupied[row + offset]?.[column]).every(
              Boolean,
            );
            if (fits) {
              found = true;
              break;
            }
          }
          if (!found) row++;
        }
        for (let offset = 0; offset < rowSpan; offset++) {
          if (!occupied[row + offset]) occupied[row + offset] = [];
          occupied[row + offset][column] = true;
        }
        return { ...item, gridRow: row, gridColumn: column };
      });
    },
    getDraggedPlacementRect(items) {
      const dragged = this.getDraggedSnapshotItem();
      const metrics = this.dragGridMetrics;
      if (!dragged || !metrics) return null;
      const placement = this.simulateDenseGrid(items).find((item) => item.id == dragged.id);
      if (!placement) return null;
      return {
        left: placement.gridColumn * (metrics.columnWidth + metrics.columnGap),
        top: placement.gridRow * (metrics.rowHeight + metrics.rowGap),
        width: metrics.columnWidth || dragged.width,
        height:
          placement.rowSpan * metrics.rowHeight + Math.max(0, placement.rowSpan - 1) * metrics.rowGap ||
          dragged.height,
      };
    },
    getDragPlacementRect(dropIndex) {
      return this.getDraggedPlacementRect(this.getDragCandidateOrder(dropIndex));
    },
    getHoveredWidgetId(clientX, clientY) {
      const target = document
        .elementFromPoint(clientX, clientY)
        ?.closest?.('.dashboard-grid-item[data-widget-id]');
      const targetId = target?.dataset?.widgetId;
      if (!targetId || targetId == this.dragWidgetId) return null;
      return this.getDragItems().some((item) => item.id == targetId) ? targetId : null;
    },
    getPointRectScore(x, y, rect) {
      const right = rect.left + rect.width;
      const bottom = rect.top + rect.height;
      const dx = x < rect.left ? rect.left - x : x > right ? x - right : 0;
      const dy = y < rect.top ? rect.top - y : y > bottom ? y - bottom : 0;
      const edgeDistance = dx * dx + dy * dy;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const centerDistance = (x - centerX) ** 2 + (y - centerY) ** 2;
      return { edgeDistance, centerDistance };
    },
    calculateDropIndexFromPoint(clientX, clientY) {
      const maxIndex = this.getDragItems().length;
      let best = null;
      for (let index = 0; index <= maxIndex; index++) {
        const rect = this.getDragPlacementRect(index);
        if (!rect) continue;
        const score = this.getPointRectScore(clientX, clientY, rect);
        const candidate = {
          index,
          ...score,
          moveDistance: Math.abs(index - this.dragOriginalDropIndex),
        };
        if (
          !best ||
          candidate.edgeDistance < best.edgeDistance ||
          (candidate.edgeDistance == best.edgeDistance && candidate.centerDistance < best.centerDistance) ||
          (candidate.edgeDistance == best.edgeDistance &&
            candidate.centerDistance == best.centerDistance &&
            candidate.moveDistance < best.moveDistance)
        ) {
          best = candidate;
        }
      }
      return best?.index ?? this.dragOriginalDropIndex;
    },
    updateDragDropIndex(point, clientX, clientY) {
      const dragged = this.getDraggedSnapshotItem();
      if (
        dragged &&
        point.x >= dragged.left &&
        point.x <= dragged.right &&
        point.y >= dragged.top &&
        point.y <= dragged.bottom
      ) {
        this.setDragDropIndex(this.dragOriginalDropIndex);
        return;
      }
      const hoveredWidgetId = this.getHoveredWidgetId(clientX, clientY);
      const targetOrder = this.getSameHeightDropOrder(hoveredWidgetId);
      if (targetOrder) {
        this.dragTargetOrder = targetOrder;
        return;
      }
      this.setDragDropIndex(this.calculateDropIndexFromPoint(point.x, point.y));
    },
    updateDragPosition(event) {
      if (!this.dragWidgetId || !event) return;
      this.dragPointerPoint = { x: event.clientX, y: event.clientY };
      this.updateDragShadow(event);
      this.updateDragDropIndex(
        this.getDashboardListPoint(event.clientX, event.clientY),
        event.clientX,
        event.clientY,
      );
      this.scheduleDragAutoScroll();
    },
    pointerMatchesDrag(event) {
      return this.dragPointerId === null || event.pointerId == this.dragPointerId;
    },
    onWidgetPointerMove(event) {
      if (!this.pointerMatchesDrag(event)) return;
      if (event.cancelable) event.preventDefault();
      this.updateDragPosition(event);
    },
    onWidgetPointerEnd(event) {
      if (!this.pointerMatchesDrag(event)) return;
      this.updateDragPosition(event);
      this.finishWidgetDrag(true);
    },
    onWidgetPointerCancel(event) {
      if (!this.pointerMatchesDrag(event)) return;
      this.finishWidgetDrag(false);
    },
    applyDragDrop() {
      if (!this.dragWidgetId) return false;

      const orderedWidgets = this.getWidgetsByGridOrder(this.widgetList || []);
      const targetOrder = this.getActiveDragOrder();
      if (!targetOrder.length) return false;
      const widgetMap = orderedWidgets.reduce((acc, widget) => {
        acc[widget.id] = widget;
        return acc;
      }, {});
      const visualList = this.dragSnapshot.map((item) => widgetMap[item.id]).filter((widget) => widget);
      const arr = targetOrder.map((item) => widgetMap[item.id]).filter((widget) => widget);
      const currentIds = visualList.map((item) => item.id).join('|');
      const nextIds = arr.map((item) => item.id).join('|');
      if (currentIds == nextIds) return false;

      const visibleIds = new Set(visualList.map((item) => item.id));
      let visibleIndex = 0;
      const merged = orderedWidgets.map((widget) => (visibleIds.has(widget.id) ? arr[visibleIndex++] : widget));
      this.widgetList = this.assignGridOrder(merged);
      return true;
    },
    removeDragListeners() {
      window.removeEventListener('pointermove', this.onWidgetPointerMove);
      window.removeEventListener('pointerup', this.onWidgetPointerEnd);
      window.removeEventListener('pointercancel', this.onWidgetPointerCancel);
    },
    finishWidgetDrag(applyDrop) {
      const changed = this.isDragging && applyDrop ? this.applyDragDrop() : false;
      this.removeDragListeners();
      this.dragWidgetId = null;
      this.dragPointerId = null;
      this.dragPointerPoint = null;
      this.dragDropIndex = null;
      this.dragOriginalDropIndex = null;
      this.dragTargetOrder = null;
      this.dragSnapshot = [];
      this.dragGridMetrics = null;
      this.isDragging = false;
      this.removeDragShadow();
      if (this.dragScrollFrame) {
        cancelAnimationFrame(this.dragScrollFrame);
        this.dragScrollFrame = null;
      }
      if (changed) this.saveDashboard();
    },
    scheduleDragAutoScroll() {
      if (!this.isDragging || !this.$refs.dashboardMain || this.dragScrollFrame) return;
      this.dragScrollFrame = requestAnimationFrame(this.runDragAutoScroll);
    },
    runDragAutoScroll() {
      this.dragScrollFrame = null;
      const container = this.$refs.dashboardMain;
      const point = this.dragPointerPoint;
      if (!this.isDragging || !container || !point) return;

      const rect = container.getBoundingClientRect();
      const edgeSize = 80;
      let scrollAmount = 0;
      if (point.y < rect.top + edgeSize) {
        scrollAmount = -Math.max(4, edgeSize - (point.y - rect.top)) / 2;
      } else if (point.y > rect.bottom - edgeSize) {
        scrollAmount = Math.max(4, edgeSize - (rect.bottom - point.y)) / 2;
      }
      if (!scrollAmount) return;

      const previousScrollTop = container.scrollTop;
      container.scrollTop += scrollAmount;
      if (container.scrollTop == previousScrollTop) return;

      const localPoint = this.getDashboardListPoint(point.x, point.y);
      this.updateDragDropIndex(localPoint, point.x, point.y);
      this.scheduleDragAutoScroll();
    },

    createDragShadow(widgetId, event) {
      this.removeDragShadow();
      const widgetEl = this.getDashboardItemElements().find((el) => el.dataset.widgetId == widgetId);
      if (!widgetEl || !event) return;

      const rect = widgetEl.getBoundingClientRect();
      const title = widgetEl.querySelector('.widget-title-text')?.innerText?.trim() || 'Moving widget';
      const icon = widgetEl.querySelector('.header-container .title > img')?.cloneNode(true);
      const shadow = document.createElement('div');
      shadow.className = 'dashboard-drag-shadow';
      const headerEl = document.createElement('div');
      headerEl.setAttribute(
        'style',
        [
          'display: flex',
          'align-items: center',
          'gap: 8px',
          'height: 2.25rem',
          'min-height: 2.25rem',
          'padding: 0.25rem 0.5rem',
          'border-bottom: 1px solid lightgray',
          'box-sizing: border-box',
        ].join(';'),
      );
      if (icon) {
        icon.style.width = '24px';
        icon.style.height = '24px';
        icon.style.objectFit = 'contain';
        headerEl.appendChild(icon);
      }
      const titleEl = document.createElement('div');
      titleEl.textContent = title;
      titleEl.style.overflow = 'hidden';
      titleEl.style.textOverflow = 'ellipsis';
      titleEl.style.whiteSpace = 'nowrap';
      headerEl.appendChild(titleEl);
      shadow.appendChild(headerEl);
      shadow.setAttribute(
        'style',
        [
          'position: fixed',
          'top: 0',
          'left: 0',
          'z-index: 9999',
          'pointer-events: none',
          `width: ${rect.width}px`,
          `height: ${rect.height}px`,
          'border: 1px solid #9daacc',
          'background: rgba(255, 255, 255, 0.92)',
          'font-size: 0.75rem',
          'font-weight: 600',
          'color: #2d3348',
          'box-sizing: border-box',
          'overflow: hidden',
          'transform: translate3d(-9999px, -9999px, 0)',
          'will-change: transform',
        ].join(';'),
      );
      document.body.appendChild(shadow);
      this.dragShadowEl = shadow;
      this.dragShadowOffset = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      this.updateDragShadow(event);
    },

    updateDragShadow(event) {
      if (!this.dragShadowEl || !event) return;
      const clientX = event.clientX;
      const clientY = event.clientY;
      if (clientX === undefined || clientY === undefined) return;

      this.dragShadowPoint = { x: clientX, y: clientY };
      if (this.dragShadowFrame) return;

      this.dragShadowFrame = requestAnimationFrame(() => {
        if (this.dragShadowEl && this.dragShadowPoint) {
          const offset = this.dragShadowOffset || { x: 0, y: 0 };
          this.dragShadowEl.style.transform = `translate3d(${this.dragShadowPoint.x - offset.x}px, ${
            this.dragShadowPoint.y - offset.y
          }px, 0)`;
        }
        this.dragShadowFrame = null;
      });
    },

    removeDragShadow() {
      if (this.dragShadowFrame) {
        cancelAnimationFrame(this.dragShadowFrame);
        this.dragShadowFrame = null;
      }
      if (this.dragShadowEl && this.dragShadowEl.parentNode) {
        this.dragShadowEl.parentNode.removeChild(this.dragShadowEl);
      }
      this.dragShadowEl = null;
      this.dragShadowPoint = null;
      this.dragShadowOffset = null;
    },

    async getDashboardAccess() {
      const result = await this.$apiCallV2('/Dashboard/GetDashboardAccess', {});
      if (this._isDestroyed) return;
      if (!result || result?.[0]?.Results == '1') {
        return;
      }
      this.dashboardAccess = result.map((item) => item.Control);
    },
  },

  async mounted() {
    if (this.dashboardSasEnabled) await this.getDashboardAccess();
    if (this._isDestroyed) return;
    if (!this.$store.state.dashboard) await this.getDashboard();
    if (this._isDestroyed) return;
    this.widgetList = this.$store.state.dashboard;

    window.addEventListener('beforeunload', this.handleUnload);
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleUnload);
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    this.cancelWidgetScroll();
    this.finishWidgetDrag(false);
  },
  created() {
    const { dashboard } = dashboardItemConfig(this.$router, {
      apiCallV2: this.$apiCallV2.bind(this),
      fullScreenLoading: this.$fullscreenLoading.bind(this),
    });
    this.dashboardItemConfigList = dashboard;
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.dashboard-main {
  width: 100%;
  max-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding-top: 0 !important;
}

.dashboard-header {
  width: 100%;
  padding-bottom: 0.75rem;
  padding-top: 12px;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 2;
  background: linear-gradient(180deg, var(--main-bg-color) 25%, transparent 100%);

  .add-widget-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  font-size: 0.9rem;
}

.dashboard-list-wrapper {
  position: relative;
  flex: 1 0 auto;

  &.is-empty {
    .dashboard-list {
      height: 100%;
      grid-auto-rows: 1fr;
    }
  }
}

.dashboard-list {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 19.5rem;
  grid-auto-flow: dense;
  gap: 0.85rem;
  min-height: 100%;
  align-content: start;
  padding-bottom: 0.5rem;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (min-width: 1920px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media screen and (min-width: 2560px) {
    grid-template-columns: repeat(auto-fill, minmax(30rem, 1fr));
  }

  .dashboard-grid-item {
    min-width: 0;
    min-height: 0;
  }

  .dashboard-widget-move {
    transition: transform 0.18s ease;
  }

  .dashboard-widget-add-highlight {
    outline: 2px solid #6c8bfd;
    outline-offset: 2px;
  }

}

.dashboard-drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  min-height: 0;

  .dashboard-drop-ghost-slot {
    position: absolute;
    top: 0;
    left: 0;

    &.is-target {
      border: 2px dashed #7f94d6;
      background: rgba(127, 148, 214, 0.08);
      box-sizing: border-box;
      will-change: transform;
    }
  }
}

.dashboard-empty-placeholder {
  grid-column: 1 / -1;
  flex: 1 0 100%;
  max-width: none !important;
}

.dashboard-json-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 0 0.5rem;

  .dashboard-json-button {
    padding: 0.35rem 0.75rem;
  }

  input {
    display: none;
  }
}

@media (max-width: 600px) {
  div {
    font-size: inherit;
  }
}

</style>
